import { useEffect, useRef, useState } from 'react';
import { CLIMB_DELIVERABLES, DeliverableCard, type DeliverableData } from './DeliverablesGallery';

/**
 * TheClimb — the merged "annotated climb" section, as a CENTERED timeline.
 *
 * A spark-green spine runs down the CENTER of the section. A spark "trailhead"
 * dot caps the top (the Education Center — the start of momentum); brass
 * milestone beads mark the four phase landings (A spark → Invention → Product
 * → Business); the real deliverable cards are STATIONS that hang off the spine,
 * alternating LEFT and RIGHT. Cards within a phase are peers: they pair across
 * the spine on one balanced row sharing a single node. A phase with an odd
 * extra card drops it to its own row, alternating side across the section so
 * the composition stays balanced. A brass seal + "A business of your own."
 * crowns the summit where the spine terminates.
 *
 * Every card is uniform width and height. Breadth (the full climb, named by the
 * phase markers + their sublabels) and depth (the legible, clickable artifacts)
 * live in one section, each module named exactly once.
 *
 * Motion: a one-time, scroll-triggered draw-down. The spine scales in from the
 * top; markers and card rows rise base→summit (staggered via a per-item --d
 * delay); nodes pop; the summit seal settles last; then permanent stillness
 * (no loop). Honors prefers-reduced-motion. Transform/opacity only — zero
 * layout shift (every card keeps its reserved box throughout).
 */

const SPARK = CLIMB_DELIVERABLES.filter((d) => d.phase === 'spark');
const INVENTION = CLIMB_DELIVERABLES.filter((d) => d.phase === 'invention');
const PRODUCT = CLIMB_DELIVERABLES.filter((d) => d.phase === 'product');
const BUSINESS = CLIMB_DELIVERABLES.filter((d) => d.phase === 'business');

interface Phase {
  name: string;
  sublabel: string;
  cards: DeliverableData[];
  seed?: boolean;
  accent?: boolean;
}

const PHASES: Phase[] = [
  {
    name: 'A spark',
    sublabel: "An observation you can't shake, taken seriously from the very first step.",
    cards: SPARK,
    seed: true,
  },
  {
    name: 'Invention',
    sublabel: 'Plus a patent search and drafting workbench, to prepare your filing.',
    cards: INVENTION,
  },
  {
    name: 'Product',
    sublabel: 'Plus 3D modeling and a prototyping marketplace, to build it.',
    cards: PRODUCT,
  },
  {
    name: 'Business',
    sublabel: 'The financial and legal foundation to stand a real company up.',
    cards: BUSINESS,
    accent: true,
  },
];

/** A row that is either a left+right pair sharing one node, or a single lone
 *  card centered on the spine. */
type RowData =
  | { row: 'pair'; left: DeliverableData; right: DeliverableData }
  | { row: 'lone'; card: DeliverableData };

/** A phase plus its card rows, so each phase owns a collapsible station group
 *  (compact = inline titles; expanded = the real deliverable cards). */
interface PhaseBlock {
  phase: Phase;
  rows: RowData[];
}

/** Pair the cards within each phase. A phase with an odd extra card drops it
 *  to its own lone row, centered ON the spine. */
function buildPhaseBlocks(): PhaseBlock[] {
  return PHASES.map((phase) => {
    const rows: RowData[] = [];
    const { cards } = phase;
    for (let i = 0; i + 1 < cards.length; i += 2) {
      rows.push({ row: 'pair', left: cards[i], right: cards[i + 1] });
    }
    if (cards.length % 2 === 1) {
      rows.push({ row: 'lone', card: cards[cards.length - 1] });
    }
    return { phase, rows };
  });
}

const PHASE_BLOCKS = buildPhaseBlocks();

export function TheClimb() {
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  // Compact by default: the arc + inline deliverable titles show, the real
  // cards are collapsed behind one global reveal. Keeps the section to ~1
  // viewport while the full proof stays one tap away.
  const [expanded, setExpanded] = useState(false);
  // `armed` gates the hidden (opacity:0) resting state, and is applied only
  // once JS mounts. If JS never runs, the section is NOT armed and renders
  // fully visible — the reveal animation is progressive enhancement, never a
  // prerequisite for seeing the content.
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    setArmed(true);
    const el = ref.current;
    if (!el) {
      // No element to observe — just show it.
      setInView(true);
      return;
    }
    // Edge trigger: fire the instant ANY pixel of the section enters, then
    // stop. A threshold-based trigger (e.g. 0.18) is unreachable on mobile,
    // where this section can be 6000px+ tall and never occupies 18% of a
    // ~850px viewport — which left the whole timeline stuck invisible on
    // phones. `threshold: 0` is always reachable regardless of height.
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' },
    );
    obs.observe(el);
    // Safety net: never let a stuck/errored observer hide content forever.
    const t = window.setTimeout(() => setInView(true), 1200);
    return () => {
      obs.disconnect();
      window.clearTimeout(t);
    };
  }, []);

  // Stagger base→summit: each item rises a beat after the previous one.
  let delay = 200;
  const nextDelay = () => {
    const d = delay;
    delay += 120;
    return d;
  };

  return (
    <section
      ref={ref}
      id="journey"
      className={`climb${armed ? ' is-armed' : ''}${inView ? ' is-in' : ''}`}
    >
      {/* Dark heartwood CHAPTER PLATE — the SEPARATOR between the cream "How it
          works" section above and this cream timeline body. Testers couldn't
          tell the two adjacent cream sections apart; a light-on-light tint won't
          survive a glance, so (per the design expert) the dark ground itself is
          the divider — the climb's masthead moves onto a full-bleed heartwood
          title card. This gives the page a clean dark→light→dark→light rhythm.
          Crisp top edge with a brass hairline seam; centered chapter title. */}
      <div className="climb__plate">
        <div className="climb__plate-inner">
          <div className="climb__eyebrow">Spark to business</div>
          <h2 className="climb__headline f-display">
            Everything it takes, from spark to a{' '}
            <span className="climb__accent">business.</span>
          </h2>
          {/* Education thesis — frames the whole climb as learn-by-doing.
              (The old support paragraph was dropped to shorten the section;
              this italic line is the stronger, more distinctive one.) */}
          <p className="climb__thesis">
            You learn to build a business by building yours, with{' '}
            <a href="/learn" className="climb__thesis-link">
              the Education Center
            </a>{' '}
            teaching the why behind every step.
          </p>
        </div>
      </div>

      <div className="climb__inner">
        {/* One global reveal — expands every stage's real deliverable cards in
            place. Sits above the timeline so the intro stays the stable anchor
            when the cards grow below it. */}
        <button
          type="button"
          className="climb__reveal"
          aria-expanded={expanded}
          aria-controls="climb-stations"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="climb__reveal-label">
            {expanded ? 'Hide the deliverables' : 'See what you build at each stage'}
          </span>
          <span className={`climb__reveal-caret${expanded ? ' is-open' : ''}`} aria-hidden="true">
            <svg viewBox="0 0 12 12" fill="none">
              <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>

        <div className={`tl${expanded ? ' is-expanded' : ''}`} id="climb-stations">
          <div className="tl__spine" aria-hidden="true" />
          {/* Dotted "lesson rail" twinned 8px left of the solid climb spine: a
              learning thread that visibly shadows the entire climb. Solid spine
              = the climb (what you do); dotted rail = the lessons (what you
              learn alongside it). Both spark-green (learning IS momentum),
              differentiated by solid vs dotted. */}
          <div className="tl__rail" aria-hidden="true" />

          {/* Trailhead — Education is the base camp that opens the climb. Built
              deliberately UNLIKE a phase marker (spark ignition emblem + mono
              eyebrow + a Sentient sentence + support + link, vs a phase's brass
              bead + one noun + sublabel) so it reads as "the start", never
              another phase. Spark green throughout = ignition/origin; brass
              stays reserved for the phase milestones and the summit seal. */}
          <div className="tl__head" style={{ ['--d' as string]: '120ms' }}>
            {/* Learning-glyph medallion: an open book with a climbing arrow
                rising out of it — the one representational icon on the whole
                timeline, marking where the always-on learning layer begins.
                All spark-green (the learning layer), zero brass (brass is for
                milestones + the summit), so it can't be mistaken for a phase. */}
            <span className="tl__head-emblem" aria-hidden="true">
              <span className="tl__head-halo" />
              <span className="tl__head-medallion">
                <svg viewBox="0 0 48 48" fill="none" className="tl__head-glyph">
                  <path d="M24 20 L7 16 V33 L24 37 Z" fill="var(--spark)" fillOpacity="0.14" stroke="var(--spark)" strokeWidth="2.2" strokeLinejoin="round" />
                  <path d="M24 20 L41 16 V33 L24 37 Z" fill="var(--spark)" fillOpacity="0.14" stroke="var(--spark)" strokeWidth="2.2" strokeLinejoin="round" />
                  <path d="M24 20 V37" stroke="var(--spark)" strokeWidth="2.2" strokeLinecap="round" />
                  <path d="M24 18 V12 H31 V6.5" fill="none" stroke="var(--spark)" strokeWidth="2.6" strokeLinejoin="round" />
                  <polygon points="38,4 30,5.5 33,11.5" fill="var(--spark)" />
                </svg>
              </span>
            </span>
            {/* The text sits on a parchment mask so the centered spine passes
                BEHIND it (same device the phase markers use) — the line never
                visibly slices through the Education Center copy. */}
            <div className="tl__head-text">
              <div className="tl__head-eyebrow">
                <span className="tl__head-eyebrow-dot" aria-hidden="true" />
                The Education Center
              </div>
              <h3 className="tl__head-title f-display">
                Learn as you <span className="tl__head-accent">climb.</span>
              </h3>
              <p className="tl__head-support">
                A lesson runs alongside every step, so you come out able to run
                the business, not just hold a patent.
              </p>
              {/* Proof element — makes the "lessons the whole way up" claim
                  concrete and ties the dotted lesson-rail to something real. */}
              <div className="tl__head-proof">
                <span className="tl__head-proof-dots" aria-hidden="true">
                  <i /><i /><i /><i />
                </span>
                <span className="tl__head-proof-text">A lesson at every step</span>
                <span className="tl__head-proof-sub">3 curriculums · 16 paths</span>
              </div>
              <a href="/learn" className="tl__head-link">
                Explore the Education Center &rarr;
              </a>
            </div>
          </div>

          {PHASE_BLOCKS.map(({ phase, rows }) => {
            const style = { ['--d' as string]: `${nextDelay()}ms` };
            const wordClass = [
              'tl__phase-word',
              'f-display',
              phase.seed ? 'tl__phase-word--seed' : '',
              phase.accent ? 'tl__phase-word--accent' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div className="tl__block" key={`p-${phase.name}`}>
                {/* Phase milestone marker */}
                <div className="tl__phase" style={style}>
                  <span
                    className={`tl__phase-node${phase.seed ? ' tl__phase-node--seed' : ''}`}
                    aria-hidden="true"
                  />
                  <span className="tl__lesson-node tl__lesson-node--phase" aria-hidden="true" />
                  <span className="tl__phase-text">
                    <span className={wordClass}>{phase.name}</span>
                    <span className="tl__phase-sub">{phase.sublabel}</span>
                  </span>
                </div>

                {/* Compact stand-in: a tight, brass-dotted list of the real
                    deliverable titles. Sells "what you get" in one line without
                    the tall cards. Hidden once the stations expand. */}
                <div className="tl__phase-deliverables">
                  {phase.cards.map((c) => (
                    <span className="tl__phase-deliverable" key={c.title}>
                      {c.title}
                    </span>
                  ))}
                </div>

                {/* Collapsible stations: the real deliverable cards, revealed
                    by the global toggle. grid 0fr→1fr collapse (no height math). */}
                <div className="tl__stations">
                  <div className="tl__stations-inner" inert={expanded ? undefined : true}>
                    {rows.map((row, ri) => (
                      <div className="tl__row" key={`r-${phase.name}-${ri}`}>
                        {row.row === 'pair' ? (
                          <>
                            <span className="tl__node" aria-hidden="true" />
                            <span className="tl__lesson-node tl__lesson-node--row" aria-hidden="true" />
                            <span className="tl__arm tl__arm--left" aria-hidden="true" />
                            <span className="tl__arm tl__arm--right" aria-hidden="true" />
                            <div className="tl__card tl__card--left">
                              <DeliverableCard {...row.left} compact />
                            </div>
                            <div className="tl__card tl__card--right">
                              <DeliverableCard {...row.right} compact />
                            </div>
                          </>
                        ) : (
                          <div className="tl__card tl__card--center">
                            <DeliverableCard {...row.card} compact />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Summit — the spine terminates in the brass seal. */}
          <div className="tl__summit" style={{ ['--d' as string]: `${delay + 120}ms` }}>
            <span className="tl__seal" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 12.5l4 4 8-9"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="tl__summit-text">
              <span className="tl__summit-word f-display">A business of your own.</span>
              <span className="tl__summit-sub">
                And commercialization support, for when you are ready to license,
                sell, or scale.
              </span>
            </span>
          </div>
        </div>

        {/* Companion rail — the quiet always-on utility. */}
        <div className="climb__rail">
          <span className="climb__rail-lead">Alongside every step</span>
          Project tracking
        </div>

        {/* Security reassurance at the journey moment. */}
        <div className="climb__secure">
          <svg className="climb__lock" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5" />
            <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
          </svg>
          <span>
            Your idea stays yours at every step. It is never used to train AI
            models, and you can delete it anytime.
          </span>
        </div>
      </div>

      <style>{CLIMB_CSS}</style>
    </section>
  );
}

const CLIMB_CSS = `
/* LIGHT-GROUND climb on parchment. The journey is now a CENTERED timeline:
   a spark spine down the middle, deliverable cards alternating left/right as
   stations, phase words as centered milestone markers. */
.climb {
  background: var(--parchment);
  /* No section padding — the dark plate butts flush against the top edge (and
     the section above) with no cream strip, and the timeline body carries its
     own padding via .climb__inner below. */
  padding: 0;
}
.climb__inner {
  max-width: 1040px;
  margin: 0 auto;
  /* Padding moved here off .climb so the plate can be full-bleed. Top gap sits
     below the plate; sides restore the gutter; bottom closes the section. */
  padding: clamp(48px, 7vh, 84px) 32px clamp(64px, 8vh, 96px);
}

/* ===== Chapter plate — the dark heartwood separator / title card ===== */
/* Full-bleed heartwood band carrying the climb's masthead. It is the visual
   break between the two cream sections. Short (a title card, not a section) so
   it never reads as a second Marketplace-style dark zone. */
.climb__plate {
  position: relative;
  background: var(--heartwood);
  padding: clamp(60px, 9vh, 96px) 32px clamp(68px, 10vh, 112px);
  text-align: center;
}
/* Brass hairline exactly on the cream→dark seam (a gilt edge). */
.climb__plate::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--brass);
  opacity: 0.55;
}
.climb__plate-inner { max-width: 760px; margin: 0 auto; }

/* Intro type — now on the dark plate, so recolored for the heartwood ground. */
.climb__eyebrow {
  font-family: var(--f-mono);
  /* Bigger + bold (was 12px / 500) for more presence. */
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  /* Brass-LIGHT on the dark plate (brass-deep would sink into the heartwood). */
  color: var(--brass-light);
  margin-bottom: 14px;
}
.climb__eyebrow::before {
  content: "";
  display: inline-block;
  width: 28px;
  height: 2px;
  background: var(--brass);
  vertical-align: middle;
  margin-right: 12px;
}
.climb__headline {
  font-size: clamp(32px, 4.4vw, 52px);
  font-weight: 400;
  line-height: 1.12;
  letter-spacing: -0.02em;
  /* Parchment on the dark plate (was ink on cream). */
  color: var(--parchment);
  max-width: 22ch;
  margin: 0 auto;
}
/* On-dark spark green for the accent word (the light-ground --spark muddies on
   heartwood; the brighter --spark-light carries the two-tone pop, matching the
   hero headline logic). */
.climb__accent { color: var(--spark-light); }
.climb__support {
  font-family: var(--f-sans);
  font-size: clamp(15px, 1.3vw, 17px);
  line-height: 1.55;
  color: color-mix(in srgb, var(--ink) 70%, transparent);
  max-width: 460px;
  margin: 14px auto 0;
}
.climb__thesis {
  font-family: var(--f-serif);
  font-style: italic;
  font-size: clamp(18px, 1.5vw, 21px);
  line-height: 1.5;
  /* Soft parchment on the dark plate (was ink on cream). */
  color: color-mix(in srgb, var(--parchment) 82%, transparent);
  max-width: 480px;
  margin: 16px auto 0;
}
.climb__thesis-link {
  /* Brass-light link on the dark plate, brass underline. */
  color: var(--brass-light);
  text-decoration: underline;
  text-decoration-color: var(--brass);
  text-underline-offset: 3px;
  text-decoration-thickness: 1.5px;
  transition: text-decoration-thickness 120ms ease;
}
.climb__thesis-link:hover { text-decoration-thickness: 2px; }

/* ===== Global reveal toggle ===== */
/* One bordered, brass-tinted bar that expands every stage's real deliverable
   cards. Sits between the intro and the timeline so the intro is the stable
   anchor when the cards grow below it. Rhymes with the pricing page's
   "Compare all features" toggle. */
.climb__reveal {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  /* No top margin — the reveal is now the first child of .climb__inner, whose
     top padding provides the gap below the plate. */
  margin: 0 auto;
  min-width: 300px;
  max-width: 100%;
  padding: 13px 22px;
  border-radius: 12px;
  border: 1.5px solid color-mix(in srgb, var(--brass) 42%, transparent);
  background: color-mix(in srgb, var(--brass) 7%, transparent);
  color: var(--brass-deep);
  font-family: var(--f-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 140ms ease, border-color 140ms ease;
}
.climb__reveal:hover {
  background: color-mix(in srgb, var(--brass) 12%, transparent);
  border-color: color-mix(in srgb, var(--brass) 60%, transparent);
}
.climb__reveal-caret {
  display: inline-flex;
  width: 12px;
  height: 12px;
  transition: transform 220ms ease;
}
.climb__reveal-caret.is-open { transform: rotate(180deg); }

/* ===== Centered timeline ===== */
.tl {
  position: relative;
  width: min(940px, 100%);
  margin: clamp(28px, 3.5vh, 42px) auto 0;
}
/* A phase and its (collapsible) stations form one block on the spine. */
.tl__block { position: relative; }

/* Compact stand-in for the tall cards: a tight, brass-dotted list of the real
   deliverable titles under each phase. Sits on a parchment mask so the centered
   spine passes behind it. Hidden once the stations expand (no duplicate). */
.tl__phase-deliverables {
  position: relative;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 7px 16px;
  justify-content: center;
  margin: 12px auto 4px;
  max-width: 460px;
  background: var(--parchment);
  padding: 2px 10px;
}
.tl__phase-deliverable {
  font-family: var(--f-sans);
  font-size: 13px;
  font-weight: 600;
  color: var(--heartwood);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.tl__phase-deliverable::before {
  content: "";
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--brass);
  flex: none;
}
.tl.is-expanded .tl__phase-deliverables { display: none; }

/* Collapsible station group per phase. Bulletproof display swap: collapsed =
   display:none (guaranteed zero height, no reliance on grid-fr collapse quirks);
   expanded = block, with the rows fading up. */
.tl__stations { display: none; }
.tl.is-expanded .tl__stations {
  display: block;
  /* A little air from the phase marker once the real cards show. */
  padding-top: 20px;
}
/* Newly revealed rows fade + rise in (a light, one-shot reveal on expand). */
.tl.is-expanded .tl__stations .tl__row {
  animation: climbStationIn 380ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes climbStationIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
/* The spine: a spark-green line down the dead center, from below the trailhead
   medallion to the summit seal. Draws in from the top on scroll. */
.tl__spine {
  position: absolute;
  left: 50%;
  /* Starts BELOW the trailhead medallion (72px emblem + 9px halo ≈ 84px) so the
     line emerges cleanly from under the base camp and never crosses/clips the
     glyph's outer halo ring. */
  top: 90px;
  bottom: 52px;
  width: 2px;
  transform: translateX(-1px) scaleY(0);
  transform-origin: top center;
  background: var(--spark);
  opacity: 0.55;
}
.climb.is-in .tl__spine {
  transform: translateX(-1px) scaleY(1);
  transition: transform 850ms cubic-bezier(0.5, 0, 0.2, 1);
}
/* Dotted lesson rail — twinned 8px left of the spine, dashed spark green. Runs
   from below the medallion to just before the summit (ends higher than the
   spine so it doesn't enter the brass summit seal — you graduate). z-index 0:
   behind the spine, nodes, connector arms, and cards. */
.tl__rail {
  position: absolute;
  left: calc(50% - 8px);
  top: 90px;
  bottom: 150px;
  width: 2px;
  transform: translateX(-1px) scaleY(0);
  transform-origin: top center;
  background-image: repeating-linear-gradient(
    to bottom,
    color-mix(in srgb, var(--spark) 48%, transparent) 0 3px,
    transparent 3px 11px
  );
  z-index: 0;
}
.climb.is-in .tl__rail {
  transform: translateX(-1px) scaleY(1);
  transition: transform 900ms cubic-bezier(0.5, 0, 0.2, 1) 120ms;
}
/* Hollow lesson node — a parchment-filled, spark-ringed dot on the dotted rail,
   twinned to each brass milestone bead / card-row node. "A lesson sits here." */
.tl__lesson-node {
  position: absolute;
  left: calc(50% - 8px);
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--parchment);
  border: 1.5px solid var(--spark);
  transform: translate(-50%, -50%) scale(0.4);
  opacity: 0;
  z-index: 2;
}
.tl__lesson-node--phase { top: 13px; }
.tl__lesson-node--row { top: 160px; }
.climb.is-in .tl__lesson-node {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  transition: opacity 260ms ease calc(var(--d, 0ms) + 160ms),
    transform 260ms cubic-bezier(0.34, 1.4, 0.64, 1) calc(var(--d, 0ms) + 160ms);
}

/* Trailhead (Education) — a spark-keyed base camp that opens the climb. */
.tl__head {
  position: relative;
  z-index: 3;
  text-align: center;
  max-width: 460px;
  /* 48px load-bearing air before the first phase — the seam that says "the
     intro ended, the climb begins". */
  margin: 0 auto;
  padding-bottom: 48px;
}
/* Parchment mask behind the trailhead copy so the centered spine passes behind
   the words instead of slicing through them. Inline-block hugs the text width
   and the section-ground parchment makes the mask invisible; only the spine is
   hidden. (On mobile the spine moves to the left rail, clear of the text, so
   this is harmlessly parchment-on-parchment there.) */
.tl__head-text {
  position: relative;
  z-index: 1;
  display: inline-block;
  background: var(--parchment);
  padding: 6px 16px;
}
/* Spark ignition emblem — a solid spark core inside a pulse halo ring. The
   halo (a ring, not a flat disc) is the tell that distinguishes the trailhead
   from the flat brass phase beads: "energy starting", not "milestone reached".
   The green spine emerges from this node's center. */
/* Learning-glyph medallion — a 72px spark-tinted disc holding the open-book +
   climbing-arrow glyph, wrapped in a faint pulse halo. The one representational
   icon on the timeline; signals "this is where learning begins" at a glance. */
.tl__head-emblem {
  position: relative;
  /* Above the text block (z-index 1): the text block's parchment mask was
     painting over the bottom arc of the halo ring, clipping the circle. */
  z-index: 4;
  display: block;
  width: 72px;
  height: 72px;
  margin: 0 auto;
}
.tl__head-halo {
  position: absolute;
  inset: -9px;
  border-radius: 50%;
  border: 1.5px solid rgba(31, 157, 99, 0.3);
  /* Opaque parchment disc fills the whole halo so the spine/rail behind the
     emblem are fully masked — the outer ring always reads as a complete circle,
     never crossed by a line. */
  background: var(--parchment);
}
.tl__head-medallion {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  /* spark-tint disc with a clear spark ring; both rings now read as complete */
  background: color-mix(in srgb, var(--spark) 8%, var(--parchment));
  border: 1.5px solid rgba(31, 157, 99, 0.55);
  display: grid;
  place-items: center;
}
.tl__head-glyph {
  width: 40px;
  height: 40px;
  display: block;
  position: relative;
}
.tl__head-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-top: 18px;
  font-family: var(--f-mono);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  /* spark, not brass — cements "start = the always-on learning layer",
     distinct from the brass phase sublabels. Deep spark (--spark-ink) so this
     small green eyebrow clears AA on cream. */
  color: var(--spark-ink);
}
.tl__head-eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--spark);
}
.tl__head-title {
  margin-top: 12px;
  /* Bigger + medium weight so the learning message reads as a real moment, not
     a quiet label — but still under the summit's brass-seal climax. */
  font-size: clamp(30px, 3.4vw, 38px);
  font-weight: 500;
  line-height: 1.04;
  letter-spacing: -0.015em;
  color: var(--ink);
}
.tl__head-accent { color: var(--spark-ink); }
.tl__head-support {
  margin-top: 12px;
  font-family: var(--f-sans);
  font-size: 14.5px;
  line-height: 1.55;
  color: color-mix(in srgb, var(--heartwood) 74%, transparent);
}
/* Proof chip — concrete "lessons the whole way up" signal. */
.tl__head-proof {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
  justify-content: center;
}
.tl__head-proof-dots {
  display: inline-flex;
  gap: 5px;
}
.tl__head-proof-dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--spark);
}
.tl__head-proof-text {
  font-family: var(--f-sans);
  font-size: 13px;
  font-weight: 600;
  color: var(--heartwood);
}
.tl__head-proof-sub {
  font-family: var(--f-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: var(--brass-deep);
}
.tl__head-link {
  display: inline-block;
  margin-top: 16px;
  font-family: var(--f-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--spark-ink);
  text-decoration: none;
}
.tl__head-link:hover { color: #0F5C34; text-decoration: underline; }

/* Phase milestone marker — a brass bead on the spine + a centered phase word
   and sublabel masked over the spine. */
.tl__phase {
  position: relative;
  z-index: 3;
  text-align: center;
  padding: 4px 0 24px;
}
.tl__phase-node {
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--brass);
  border: 3px solid var(--parchment);
  margin: 0 auto 12px;
}
.tl__phase-node--seed { background: var(--brass); }
/* The text block carries a parchment background so the spine is masked behind
   the words (the line passes the bead, then the words float in a clear gap). */
.tl__phase-text {
  display: inline-block;
  background: var(--parchment);
  padding: 0 18px;
  max-width: 520px;
}
.tl__phase-word {
  display: block;
  font-size: clamp(22px, 2.4vw, 28px);
  font-weight: 500;
  line-height: 1.1;
  color: var(--ink);
}
.tl__phase-word--seed {
  color: var(--brass-deep);
  font-size: clamp(20px, 2.1vw, 25px);
}
.tl__phase-word--accent { color: var(--spark-ink); }
.tl__phase-sub {
  display: block;
  font-family: var(--f-sans);
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-decorative);
  margin-top: 7px;
}

/* Card row — holds the absolutely-positioned cards on each side, plus the
   shared node and connector arm(s). Fixed height keeps every card uniform and
   reserves the box so animation never shifts layout. */
.tl__row {
  position: relative;
  height: 320px;
  margin-bottom: 32px;
}
.tl__node {
  position: absolute;
  left: 50%;
  top: 160px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--brass);
  border: 2px solid var(--parchment);
  transform: translate(-50%, -50%) scale(0.4);
  opacity: 0;
  z-index: 2;
}
.tl__arm {
  position: absolute;
  top: 160px;
  height: 1.5px;
  background: color-mix(in srgb, var(--spark) 50%, transparent);
  z-index: 1;
  opacity: 0;
}
.tl__arm--left { left: calc(50% - 50px); width: 50px; }
.tl__arm--right { left: 50%; width: 50px; }

.tl__card {
  position: absolute;
  top: 0;
  /* 100px center channel (50px connector each side); identical width for every
     card at any viewport, so "uniform" holds as the container flexes. */
  width: calc((100% - 100px) / 2);
  height: 320px;
}
.tl__card--left { left: 0; }
.tl__card--right { right: 0; }
/* A lone card sits centered ON the spine. It's opaque, so it masks the spine
   behind its body — the line runs into its top edge and out its bottom,
   threading the card onto the path (no side node or arm needed). */
.tl__card--center { left: 50%; transform: translateX(-50%); }
.tl__card .deliverable-card {
  width: 100%;
  height: 100%;
  box-shadow: 0 2px 4px rgba(15, 23, 20, 0.05),
    0 12px 30px rgba(15, 23, 20, 0.10) !important;
}
/* Compact climb CTA is a brass-outline pill button — on card hover it fills
   solid brass with parchment text, the standard "this is clickable" affordance. */
.tl__card .deliverable-card:hover .deliverable-cta--compact {
  background: var(--brass) !important;
  border-color: var(--brass) !important;
  color: var(--parchment) !important;
}

/* Summit — the spine terminates in the brass seal. */
.tl__summit {
  position: relative;
  z-index: 3;
  text-align: center;
  padding-top: 4px;
}
.tl__seal {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--brass);
  color: var(--parchment);
  border: 3px solid var(--parchment);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--brass) 16%, transparent);
  margin: 0 auto 14px;
  transform: scale(0.5);
  opacity: 0;
}
.tl__seal svg { width: 20px; height: 20px; display: block; }
.tl__summit-text {
  display: inline-block;
  background: var(--parchment);
  padding: 0 18px;
  max-width: 540px;
}
.tl__summit-word {
  display: block;
  font-size: clamp(26px, 3vw, 36px);
  font-weight: 400;
  line-height: 1.1;
  color: var(--ink);
}
.tl__summit-sub {
  display: block;
  font-family: var(--f-sans);
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-decorative);
  margin-top: 8px;
}

/* ===== Entrance animation (one-time, scroll-triggered) =====
   The hidden resting state is scoped to .climb.is-armed, a class JS adds on
   mount. Without JS (or before mount) nothing is armed, so everything renders
   visible: the reveal is enhancement, never a gate on the content. */
.climb.is-armed .tl__head,
.climb.is-armed .tl__phase,
.climb.is-armed .tl__row,
.climb.is-armed .tl__summit {
  opacity: 0;
  transform: translateY(14px);
}
.climb.is-in .tl__head,
.climb.is-in .tl__phase,
.climb.is-in .tl__row,
.climb.is-in .tl__summit {
  opacity: 1;
  transform: none;
  transition: opacity 480ms cubic-bezier(0.22, 1, 0.36, 1) var(--d, 0ms),
    transform 480ms cubic-bezier(0.22, 1, 0.36, 1) var(--d, 0ms);
}
/* Nodes pop a beat after their row/marker rises. (The trailhead emblem is not
   here — it fades in with its parent .tl__head block and gets a one-time
   ignition pulse instead.) */
.climb.is-in .tl__node {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  transition: opacity 260ms ease calc(var(--d, 0ms) + 120ms),
    transform 260ms cubic-bezier(0.34, 1.4, 0.64, 1) calc(var(--d, 0ms) + 120ms);
}
/* phase-node isn't translate-centered (it's centered by margin auto), so reset
   its transform without the -50%. */
.climb.is-in .tl__phase-node {
  opacity: 1;
  transform: scale(1);
  transition: opacity 260ms ease calc(var(--d, 0ms) + 120ms),
    transform 260ms cubic-bezier(0.34, 1.4, 0.64, 1) calc(var(--d, 0ms) + 120ms);
}
.tl__phase-node { transform: scale(0.4); opacity: 0; }
/* One-time ignition pulse on the trailhead halo when the climb enters — fires
   once, then permanent stillness (a looping pulse would nag like a
   notification). */
.climb.is-in .tl__head-halo {
  animation: tlIgnite 700ms ease-out calc(var(--d, 0ms) + 200ms) 1;
}
@keyframes tlIgnite {
  0%   { transform: scale(1);    opacity: 0.35; }
  50%  { transform: scale(1.18); opacity: 0.1; }
  100% { transform: scale(1);    opacity: 1; }
}
.climb.is-in .tl__arm {
  opacity: 1;
  transition: opacity 320ms ease calc(var(--d, 0ms) + 160ms);
}
.climb.is-in .tl__seal {
  opacity: 1;
  transform: scale(1);
  transition: opacity 360ms ease var(--d, 0ms),
    transform 360ms cubic-bezier(0.34, 1.4, 0.64, 1) var(--d, 0ms);
}

/* Companion rail + security */
.climb__rail {
  margin-top: clamp(40px, 5vh, 56px);
  text-align: center;
  font-family: var(--f-mono);
  font-size: 12px;
  letter-spacing: 0.04em;
  /* ink 55% was 3.90:1 on parchment; --text-decorative (ink 0.66) is 5.57:1.
     Same token swap the first WCAG pass used elsewhere — this occurrence was
     missed. .climb__rail-lead below is --brass-deep (5.01:1) and already passes. */
  color: var(--text-decorative);
}
.climb__rail-lead {
  color: var(--brass-deep);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-right: 12px;
}
.climb__secure {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  font-family: var(--f-sans);
  font-size: 14px;
  line-height: 1.5;
  color: color-mix(in srgb, var(--ink) 62%, transparent);
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
}
.climb__lock {
  width: 19px;
  height: 19px;
  flex: none;
  stroke: var(--heartwood);
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* ===== Phone: collapse to a single left rail (≤760px) =====
   The centered two-sided timeline is the section's signature and holds down to
   ~760px (paired cards ~302px at 768px). The tablet band (761–1024) keeps it
   centered; only true phones drop to the left rail. */
@media (max-width: 760px) {
  .tl { width: 100%; }
  /* Spine on the left rail; starts below the 48px mobile medallion. */
  .tl__spine { left: 19px; top: 56px; }
  /* On a narrow screen the twin dotted rail + lesson nodes crowd the stacked
     cards, so drop them (the designer-sanctioned mobile fallback) — the single
     spine carries the climb. */
  .tl__rail { display: none; }
  .tl__lesson-node { display: none; }

  /* Markers/head/summit go left-aligned; their node/seal is pinned ON the rail
     (absolute, left = spine), and the text clears the rail via padding. The
     nodes position by left/top only (transform carries scale alone), so the
     reduced-motion override can't knock them off the rail. */
  .tl__head, .tl__phase, .tl__summit {
    position: relative;
    text-align: left;
    max-width: none;
    margin: 0;
    padding: 4px 0 18px 52px;
  }
  /* Trailhead medallion on mobile: smaller (48px) and centered on the left rail
     (center at x=19 → left = 19 - 24 = -5px). */
  .tl__head-emblem {
    position: absolute;
    left: -5px;
    top: 0;
    width: 48px;
    height: 48px;
    margin: 0;
  }
  .tl__head-glyph { width: 26px; height: 26px; }
  .tl__phase-node {
    position: absolute;
    left: 10px;
    top: 4px;
    margin: 0;
  }
  .tl__seal {
    position: absolute;
    left: 1px;
    top: 0;
    margin: 0;
  }
  .tl__phase-text, .tl__summit-text, .tl__head-text {
    background: none;
    padding: 0;
    display: block;
    max-width: none;
  }

  /* Compact deliverable list: left-aligned under the phase text, clearing the
     left rail (matches the phase/summit padding), no parchment mask needed. */
  .tl__phase-deliverables {
    justify-content: flex-start;
    text-align: left;
    max-width: none;
    margin: 8px 0 0;
    padding: 0 0 0 52px;
    background: none;
    gap: 6px 14px;
  }
  .tl__phase-deliverable { white-space: normal; }

  /* Cards become a single stacked column to the right of the rail. */
  .tl__row { height: auto; margin-bottom: 16px; }
  .tl__card {
    position: static;
    width: calc(100% - 52px);
    height: auto;
    margin-left: 52px;
  }
  /* Reset the desktop centering transform so a lone card stacks in the rail. */
  .tl__card--center { left: auto; transform: none; }
  .tl__card + .tl__card { margin-top: 16px; }
  .tl__card .deliverable-card { height: auto; }
  .tl__arm { display: none; }
  /* Card-row node on the rail; position via left/top, scale-only transform. */
  .tl__node { left: 12px; top: 30px; transform: scale(0.4); }
  .climb.is-in .tl__node { transform: scale(1); }
}

/* ===== Tablet portrait (761–1024): hold the centered two-sided timeline, just
   narrower. Tighten section side padding and shrink the center channel from
   100px → 64px so the paired cards buy back width (~328px at 768px, ~336px at
   800px) and never feel cramped. The fixed 320px card height holds — don't
   switch to auto here, or paired stations stop sitting level. */
@media (min-width: 761px) and (max-width: 1024px) {
  /* Keep .climb at padding:0 so the plate stays full-bleed; tighten the body
     gutter on .climb__inner instead (the plate keeps its own padding). */
  .climb__inner { padding: clamp(40px, 6vh, 72px) 24px clamp(56px, 8vh, 88px); }
  .tl__card { width: calc((100% - 64px) / 2); }
  .tl__arm--left { left: calc(50% - 32px); width: 32px; }
  .tl__arm--right { left: 50%; width: 32px; }
}

/* Phone: the global landing rule ".landing-page-body > section:not(.landing-hero)"
   forces 20px/72px padding on EVERY body section at ≤640px (with !important),
   which would inset this section's dark chapter plate and strand a cream strip
   above it. Override via the section's #journey id (an id beats that class rule
   cleanly) so .climb stays padding:0 and the plate is full-bleed + flush; the
   body gutter moves onto .climb__inner, and the plate keeps its own padding. */
@media (max-width: 640px) {
  #journey { padding: 0 !important; }
  .climb__inner { padding: clamp(36px, 6vh, 56px) 20px 60px; }
  .climb__plate { padding: clamp(48px, 7vh, 72px) 24px clamp(52px, 8vh, 84px); }
}

@media (prefers-reduced-motion: reduce) {
  .tl__spine, .tl__rail { transform: translateX(-1px) scaleY(1); transition: none; }
  .tl__head, .tl__phase, .tl__row, .tl__summit,
  .tl__node, .tl__phase-node, .tl__arm, .tl__seal, .tl__lesson-node {
    opacity: 1;
    transition: none;
  }
  .tl__head, .tl__phase, .tl__row, .tl__summit { transform: none; }
  .tl__node { transform: translate(-50%, -50%) scale(1); }
  .tl__lesson-node { transform: translate(-50%, -50%) scale(1); }
  .tl__phase-node { transform: scale(1); }
  .tl__seal { transform: scale(1); }
  /* No ignition pulse under reduced motion. */
  .climb.is-in .tl__head-halo { animation: none; }
  /* Expand/collapse snaps instead of animating. */
  .tl.is-expanded .tl__stations .tl__row { animation: none; }
  .climb__reveal-caret { transition: none; }
}

/* Decision-funnel filter line on each compact climb station card — a "gate
   stamp": a mono brass question (the filter criterion) over a muted sans
   consequence (the expensive mistake it spares you), marked by a left brass
   rule that rhymes with the spine. min-height pins it to two lines so every
   card grows by the same amount and left/right stations stay level. */
.deliverable-filter {
  margin-top: 10px;
  min-height: 34px;
  padding-left: 11px;
  border-left: 2px solid color-mix(in srgb, var(--brass) 50%, transparent);
  font-family: var(--f-sans);
  font-size: 12.5px;
  line-height: 1.4;
  color: rgba(15, 23, 20, 0.62);
}
.deliverable-filter__q {
  display: block;
  font-family: var(--f-mono);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brass-deep);
  margin-bottom: 2px;
}
`;
